import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Panel } from '../../components/Panel';
import { Button } from '../../components/Button';
import { uploadImages, deleteUpload, listStaff } from '../../api/endpoints';
import { isProbablyStillProcessing, userErrorMessage } from '../../api/client';
import { useCurrentStaff } from '../../hooks/useCurrentStaff';
import { formatJST } from '../../utils/format';
import type { Upload, Staff } from '../../types/api';

const VALID_EXTS = ['.pdf', '.jpg', '.jpeg', '.png', '.webp', '.tiff', '.tif', '.xlsx'];
type DocType = 'order' | 'estimate' | 'estimate_maker';

// これより長くかかってから通信が切れたら、サーバーで読み取りが続いているとみなす
// （Azure は約230秒で通信を切る。数秒で切れるのはサーバーに届いていないとき）
const STILL_PROCESSING_AFTER_MS = 60_000;

export function UploadPage() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 通信は切れたがサーバーで読み取りが続いている（大量明細で約230秒を超えた）
  const [stillProcessing, setStillProcessing] = useState(false);
  const [uploader, setUploader] = useState('');
  const [note, setNote] = useState('');
  const [docType, setDocType] = useState<DocType>('order');
  const [skipSearch, setSkipSearch] = useState(false);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const { staffCode: myStaffCode, locked: staffLocked } = useCurrentStaff();

  useEffect(() => {
    void (async () => {
      try {
        const data = await listStaff();
        setStaffList(data.items);
      } catch { /* silent */ }
    })();
  }, []);

  // ログイン中はログインユーザーの担当者コードを入力担当者に固定する
  useEffect(() => {
    if (staffLocked && myStaffCode) setUploader(myStaffCode);
  }, [staffLocked, myStaffCode]);

  const matchedStaff = uploader.length === 4
    ? staffList.find((s) => s.code === uploader) ?? null
    : null;
  const staffName = matchedStaff
    ? `${matchedStaff.name}（${matchedStaff.department}）`
    : '';

  function handleFileSelect(files: FileList | null) {
    if (!files || files.length === 0) return;
    const validFiles = Array.from(files).filter((f) =>
      VALID_EXTS.some((ext) => f.name.toLowerCase().endsWith(ext)),
    );
    if (validFiles.length === 0) {
      setError('対応していないファイル形式です');
      return;
    }
    setError(null);
    setStagedFiles(validFiles);
  }

  function removeStagedFile(index: number) {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleUpload() {
    if (!uploader.trim()) {
      setError('入力担当者コードは必須です');
      return;
    }
    if (stagedFiles.length === 0) {
      setError('ファイルを選択してください');
      return;
    }

    setUploading(true);
    setError(null);
    setStillProcessing(false);
    const startedAt = Date.now();
    try {
      const result = await uploadImages(stagedFiles, { uploader, note, doc_type: docType, skipSearch });
      setUploads((prev) => [result, ...prev]);
      setStagedFiles([]);
    } catch (e) {
      // すぐに切れた場合はサーバーに届いていない（未接続など）ので通常のエラーにする
      if (isProbablyStillProcessing(e) && Date.now() - startedAt >= STILL_PROCESSING_AFTER_MS) {
        // サーバーは読み取りを続けて OCR 画面の一覧に結果を出す。同じファイルを
        // 二重に送らないよう、選択中のファイルは外しておく。
        setStillProcessing(true);
        setStagedFiles([]);
      } else {
        setError(userErrorMessage(e));
      }
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('このアップロードを削除しますか？')) return;
    try {
      await deleteUpload(id);
      setUploads((prev) => prev.filter((u) => u.id !== id));
    } catch (e) {
      setError(userErrorMessage(e));
    }
  }

  const canUpload = uploader.trim() !== '' && stagedFiles.length > 0 && !uploading;

  return (
    <>
      <Panel title="アップロード">
        {/* 入力フォーム */}
        <div className="mb-4 space-y-3">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium whitespace-nowrap">
                種別<span className="text-danger">*</span>:
              </label>
              <select
                className="border rounded px-3 py-1.5 text-sm"
                value={docType}
                onChange={(e) => setDocType(e.target.value as DocType)}
              >
                <option value="order">注文書</option>
                <option value="estimate">見積依頼</option>
                <option value="estimate_maker">メーカー見積</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium whitespace-nowrap">
                入力担当者<span className="text-danger">*</span>:
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={4}
                readOnly={staffLocked}
                title={staffLocked ? 'ログイン中のユーザーで自動設定されます' : undefined}
                className={`border rounded px-3 py-1.5 text-sm w-20 font-mono ${
                  staffLocked ? 'bg-slate-100 text-muted cursor-not-allowed' : ''
                } ${error && !uploader.trim() ? 'border-danger' : ''}`}
                placeholder="0000"
                value={uploader}
                onChange={(e) => {
                  if (staffLocked) return;
                  const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                  setUploader(v);
                }}
                onBlur={() => {
                  if (staffLocked) return;
                  if (uploader && uploader.length < 4) setUploader(uploader.padStart(4, '0'));
                }}
              />
              {staffName && <span className="text-sm text-muted">{staffName}</span>}
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer" title="マスタ照合をせず、OCR抽出のみ行います（見積汎用コード一括登録などで使用）">
              <input type="checkbox" checked={skipSearch} onChange={(e) => setSkipSearch(e.target.checked)} />
              <span>商品検索をしない（OCRのみ）</span>
            </label>
          </div>
          <div className="flex items-start gap-2">
            <label className="text-sm font-medium whitespace-nowrap mt-1.5">備考:</label>
            <textarea
              className="border rounded px-3 py-1.5 text-sm w-full min-h-[60px]"
              placeholder="メモ・補足情報など"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>

        {/* ファイル選択エリア */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-primary bg-blue-50' : 'border-border hover:border-primary'
          }`}
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFileSelect(e.dataTransfer.files);
          }}
        >
          <p className="text-slate-600">
            ファイルをドラッグ&ドロップ、またはクリックして選択
          </p>
          <p className="text-xs text-muted mt-1">
            PDF / JPEG / PNG / Excel(.xlsx)（複数選択で1グループとして登録）
          </p>
          <input
            ref={inputRef}
            type="file"
            accept={VALID_EXTS.join(',')}
            multiple
            hidden
            onChange={(e) => {
              handleFileSelect(e.target.files);
              e.target.value = '';
            }}
          />
        </div>

        {/* ステージングされたファイル一覧 */}
        {stagedFiles.length > 0 && (
          <div className="mt-4 border rounded p-3 bg-slate-50">
            <p className="text-sm font-medium mb-2">選択中のファイル（{stagedFiles.length}件）</p>
            <div className="flex flex-wrap gap-3 mb-3">
              {stagedFiles.map((f, i) => {
                const url = URL.createObjectURL(f);
                const isImage = f.type.startsWith('image/');
                const isExcel = f.name.toLowerCase().endsWith('.xlsx');
                return (
                  <div key={i} className="flex flex-col items-center gap-1 bg-white border rounded p-2">
                    <a href={url} target="_blank" rel="noopener noreferrer" title="クリックでプレビュー">
                      {isImage ? (
                        <img src={url} alt={f.name} className="w-16 h-16 object-cover rounded cursor-pointer hover:ring-2 hover:ring-primary" />
                      ) : (
                        <div className="w-16 h-16 bg-slate-100 rounded flex items-center justify-center text-xs text-muted cursor-pointer hover:ring-2 hover:ring-primary">{isExcel ? 'Excel' : 'PDF'}</div>
                      )}
                    </a>
                    <span className="font-mono text-xs max-w-[120px] truncate" title={f.name}>{f.name}</span>
                    <button
                      className="text-xs text-slate-400 hover:text-danger"
                      onClick={() => removeStagedFile(i)}
                    >
                      除外
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => void handleUpload()}
                disabled={!canUpload}
              >
                {uploading ? 'アップロード + OCR 読取中...' : 'アップロード'}
              </Button>
              {!uploader.trim() && (
                <span className="text-sm text-danger">入力担当者コードを入力してください</span>
              )}
            </div>
          </div>
        )}

        {error && <p className="text-danger text-sm mt-2">{error}</p>}
        {stillProcessing && (
          <div className="mt-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <p>
              明細が多く、読み取りに時間がかかっているため、画面との通信が途中で切れました。
              読み取りはサーバーで続いており、終わると「OCR」画面の一覧に表示されます（数分かかることがあります）。
            </p>
            <p className="mt-1">もう一度アップロードすると同じ書類が二重に登録されるため、一覧に表示されるまでお待ちください。</p>
            <Button variant="secondary" className="mt-2" onClick={() => navigate('/ocr')}>
              OCR画面の一覧を開く
            </Button>
          </div>
        )}
      </Panel>

      {uploads.length > 0 && (
        <Panel title={`アップロード一覧（${uploads.length}件）`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="pb-2 pr-4">種別</th>
                  <th className="pb-2 pr-4">ファイル名</th>
                  <th className="pb-2 pr-4">入力担当者</th>
                  <th className="pb-2 pr-4">備考</th>
                  <th className="pb-2 pr-4">登録日時</th>
                  <th className="pb-2">操作</th>
                </tr>
              </thead>
              <tbody>
                {uploads.map((u) => (
                  <tr key={u.id} className="border-b last:border-0">
                    <td className="py-2 pr-4 text-xs">
                      {u.doc_type === 'estimate_maker' ? (
                        <span className="inline-block px-2 py-0.5 rounded bg-purple-100 text-purple-800">メーカー見積</span>
                      ) : u.doc_type === 'estimate' ? (
                        <span className="inline-block px-2 py-0.5 rounded bg-purple-100 text-purple-800">見積</span>
                      ) : (
                        <span className="inline-block px-2 py-0.5 rounded bg-blue-100 text-blue-800">注文</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-xs">
                      <span className="font-mono">{u.files.map((f) => f.filename).join(', ')}</span>
                      {u.files.length > 1 && (
                        <span className="ml-1 text-muted">({u.files.length}枚)</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-xs">
                      {(() => {
                        const s = staffList.find((s) => s.code === (u.uploader ?? ''));
                        const name = s ? `${s.name}（${s.department}）` : undefined;
                        return name || u.uploader || '-';
                      })()}
                    </td>
                    <td className="py-2 pr-4 text-xs text-muted">{u.note || '-'}</td>
                    <td className="py-2 pr-4 text-xs text-muted">{formatJST(u.created_at)}</td>
                    <td className="py-2">
                      <div className="flex gap-1">
                        <Button
                          variant="primary"
                          className="text-xs px-2 py-1"
                          onClick={() => navigate(`/ocr?uploadId=${u.id}`)}
                        >
                          OCR 実行
                        </Button>
                        <Button
                          variant="danger"
                          className="text-xs px-2 py-1"
                          onClick={() => void handleDelete(u.id)}
                        >
                          削除
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </>
  );
}
